import { SolutionScenario } from '@/types/scenarios/solution';
import { Factory, Phone, Calendar, Users, Wrench, Package, Bell, ClipboardList, Truck, Shield } from 'lucide-react';

export const manufacturing: SolutionScenario = {
  meta: {
    id: 'manufacturing',
    slug: 'manufacturing',
    name: 'Manufacturing',
    tagline: 'Genies that handle order status, RFQs, and tier one tech support',
    description: "A genie that handles order status, RFQs, and tier one technical questions so your sales engineers and reps stop fielding the same calls every shift.",
    market: 'Manufacturing',
    subMarkets: ['OEM Manufacturing', 'Contract Manufacturing', 'Food & Beverage', 'Electronics', 'Metal Fabrication', 'Plastics & Packaging'],
    specificNeeds: [
      '24/7 order status inquiries',
      'Technical support hotlines',
      'Quality issue reporting',
      'Shipment tracking assistance',
      'Quote and RFQ handling',
      'Production scheduling inquiries'
    ],
    commonRoles: ['Customer Service Reps', 'Sales Engineers', 'Production Managers', 'Quality Assurance'],
    marketSize: '$16.5 trillion globally',
    isFeatured: true
  },

  hero: {
    headline: 'Tier one support, every call',
    headlineGradient: 'Tier one support,',
    headlineRest: 'every call',
    subheadline: "A genie that handles order status, RFQs, and tier one technical questions so your sales engineers and reps stop answering the same calls every shift.",
  },

  recommendedPlaybooks: [
    'lead-capture',
    'appointment-scheduler',
    'interactive-faq',
    'proactive-welcome'
  ],

  applications: [
    {
      id: 'order-management',
      name: 'Order Status & Management',
      description: 'Handle order inquiries, status updates, and modifications around the clock',
      department: 'Customer Service',
      icon: Package,
      playbookIds: ['interactive-faq', 'proactive-welcome'],
      benefits: [
        'Instant order status updates 24/7',
        'Automated shipment tracking',
        'Order modification requests',
        'Reduce call center volume by 50%'
      ]
    },
    {
      id: 'technical-support',
      name: 'Technical Support Hotline',
      description: 'Front line technical support for product issues and troubleshooting',
      department: 'Technical Support',
      icon: Wrench,
      playbookIds: ['interactive-faq'],
      benefits: [
        '24/7 technical assistance',
        'Guided troubleshooting steps',
        'Warranty claim initiation',
        'Escalation to engineers when needed'
      ]
    },
    {
      id: 'quote-requests',
      name: 'Quote & RFQ Handling',
      description: 'Capture and process quote requests and specifications from potential customers',
      department: 'Sales',
      icon: ClipboardList,
      playbookIds: ['lead-capture'],
      benefits: [
        'Capture RFQs after hours',
        'Gather specifications upfront',
        'Qualify leads automatically',
        'Faster quote turnaround'
      ]
    },
    {
      id: 'quality-reporting',
      name: 'Quality Issue Reporting',
      description: 'Enable customers to report quality issues and track resolution',
      department: 'Quality Assurance',
      icon: Shield,
      playbookIds: ['interactive-faq', 'lead-capture'],
      benefits: [
        'Structured issue intake',
        'Automatic ticket creation',
        'Track resolution status',
        'Improve quality feedback loop'
      ]
    },
    {
      id: 'logistics-coordination',
      name: 'Logistics & Shipping',
      description: 'Coordinate deliveries, pickups, and shipment scheduling',
      department: 'Logistics',
      icon: Truck,
      playbookIds: ['appointment-scheduler'],
      benefits: [
        'Schedule delivery windows',
        'Coordinate pickup times',
        'Track shipment status',
        'Reduce logistics calls by 40%'
      ]
    },
    {
      id: 'vendor-inquiries',
      name: 'Vendor & Supplier Support',
      description: 'Handle supplier inquiries about POs, payments, and specifications',
      department: 'Procurement',
      icon: Users,
      playbookIds: ['interactive-faq'],
      benefits: [
        'PO status inquiries',
        'Payment status updates',
        'Spec clarifications',
        'Vendor onboarding support'
      ]
    }
  ],

  challenges: [
    {
      title: 'After Hours Customer Inquiries',
      description: 'Manufacturing customers often operate different shifts and need support outside standard hours',
      impact: 'high',
      affectedAreas: ['Customer satisfaction', 'Order accuracy', 'Response time']
    },
    {
      title: 'High Volume Technical Calls',
      description: 'Technical support teams overwhelmed with routine troubleshooting requests',
      impact: 'high',
      affectedAreas: ['Support costs', 'Engineer productivity', 'Resolution time']
    },
    {
      title: 'Order Status Overload',
      description: 'Customer service teams spend significant time on repetitive order status inquiries',
      impact: 'medium',
      affectedAreas: ['Staff efficiency', 'Customer wait times', 'Operating costs']
    },
    {
      title: 'Global Customer Base',
      description: 'Serving customers across time zones requires extended support coverage',
      impact: 'high',
      affectedAreas: ['Market reach', 'Customer experience', 'Staffing costs']
    }
  ],

  solutions: [
    {
      challenge: 'After hours customer support gaps',
      solution: 'Deploy 24/7 Voice Genie for order and shipping inquiries',
      implementation: 'Integrate with ERP/order management system for real-time status access',
      expectedResults: [
        '60% reduction in missed inquiries',
        'Same-day response rate improvement',
        'Higher customer satisfaction scores'
      ]
    },
    {
      challenge: 'Technical support bottlenecks',
      solution: 'Implement front line technical triage with Voice Genie',
      implementation: 'Build knowledge base of common issues and troubleshooting steps',
      expectedResults: [
        '45% of technical calls resolved without escalation',
        'Faster time to resolution',
        'Engineers focus on complex issues'
      ]
    },
    {
      challenge: 'Quote request delays',
      solution: 'Automate RFQ intake and qualification',
      implementation: 'Voice-guided specification gathering with CRM integration',
      expectedResults: [
        '35% faster quote turnaround',
        'Complete spec capture upfront',
        'Improved win rates'
      ]
    }
  ],

  integrations: {
    marketSpecific: [
      {
        name: 'SAP',
        category: 'ERP',
        description: 'Enterprise resource planning for manufacturing operations',
        required: false,
        popularityScore: 92
      },
      {
        name: 'Oracle NetSuite',
        category: 'ERP',
        description: 'Cloud ERP for growing manufacturers',
        required: false,
        popularityScore: 85
      },
      {
        name: 'Epicor',
        category: 'ERP',
        description: 'Manufacturing-focused ERP solution',
        required: false,
        popularityScore: 78
      },
      {
        name: 'Fishbowl',
        category: 'Inventory',
        description: 'Inventory management for manufacturers',
        required: false,
        popularityScore: 72
      }
    ],
    common: [
      {
        name: 'Salesforce',
        category: 'CRM',
        description: 'Enterprise CRM platform',
        required: false,
        popularityScore: 95
      },
      {
        name: 'Microsoft Dynamics',
        category: 'ERP/CRM',
        description: 'Business applications suite',
        required: false,
        popularityScore: 88
      }
    ]
  },

  metrics: {
    averageImplementationTime: '2-3 weeks',
    typicalROI: '380%',
    adoptionRate: '91% customer satisfaction',
    customerSatisfaction: '4.7/5'
  },

  demoConfig: {
    firstMessage: "Hello! Thanks for calling. I can help you with order status, technical support, shipping inquiries, or connect you with our sales team for quotes. How can I assist you today?",
    systemPrompt: "You are a helpful manufacturing customer service assistant. Help customers with order status, technical troubleshooting, shipping inquiries, and quote requests. Be knowledgeable about common manufacturing terms and processes. Always aim to resolve inquiries efficiently while providing accurate information.",
    marketContext: "Manufacturing company handling B2B customer service and technical support",
    exampleScenarios: [
      "What's the status of my order PO-2024-1234?",
      "I'm having an issue with the product specifications",
      "When will my shipment arrive?",
      "I need a quote for 500 units"
    ]
  },

  caseStudies: [
    {
      companyName: 'Precision Parts Manufacturing',
      companySize: '3 facilities, 500+ employees',
      location: 'Midwest United States',
      challenge: 'Customer service team overwhelmed with order status calls, missing after hours inquiries from global customers',
      implementation: 'Deployed Help Genie for 24/7 order status, technical support triage, and RFQ capture',
      timeline: '3 weeks',
      results: [
        {
          metric: 'Call Volume Reduction',
          value: '52%',
          improvement: 'Routine calls handled by Genie'
        },
        {
          metric: 'After Hours Capture',
          value: '+65%',
          improvement: 'Inquiries captured outside business hours'
        },
        {
          metric: 'Quote Response Time',
          value: '-40%',
          improvement: 'Faster turnaround on RFQs'
        },
        {
          metric: 'Customer Satisfaction',
          value: '4.6/5',
          improvement: 'Up from 3.9'
        }
      ],
      quote: {
        text: "Help Genie transformed our customer service operations. We're now serving our global customers around the clock without adding headcount, and our team can focus on complex issues that truly need their expertise.",
        author: 'Sarah Chen',
        title: 'VP of Customer Operations'
      }
    }
  ],

  resources: [
    {
      type: 'guide',
      title: 'Manufacturing Voice Genie Playbook',
      description: 'Complete guide to implementing voice technology in manufacturing operations',
      url: '/resources/manufacturing-voice-guide'
    },
    {
      type: 'webinar',
      title: 'Scaling Customer Support in Manufacturing',
      description: 'Learn how manufacturers reduce support costs while improving satisfaction',
      url: '/webinars/manufacturing-support'
    },
    {
      type: 'template',
      title: 'Technical Support Scripts',
      description: 'Pre-built voice scripts for common manufacturing support scenarios',
      url: '/templates/manufacturing-support'
    }
  ],

  faqs: [
    {
      question: 'Can the genie access our ERP system for order information?',
      answer: 'Yes, Help Genie integrates with major ERP systems including SAP, Oracle NetSuite, Epicor, and Microsoft Dynamics to provide real-time order status, shipping information, and inventory data.'
    },
    {
      question: 'How does it handle technical support requests?',
      answer: 'The genie uses your product knowledge base to guide customers through common troubleshooting steps. For complex issues, it captures detailed information and escalates to your technical team with full context.'
    },
    {
      question: 'Can it handle B2B customers with specific account requirements?',
      answer: 'Absolutely. Help Genie can verify customer accounts, access account-specific pricing and terms, and handle the nuanced requirements of B2B manufacturing relationships.'
    }
  ],

  pageContent: {
    scenariosHeader: {
      title: "Your customers operate 24/7\nyour support should too",
      subtitle: "See how your Genie handles real manufacturing calls — tracking orders, triaging technical issues, and capturing RFQs from customers across every time zone.",
    },
    chatScenarios: [
      {
        id: 'order_status',
        title: 'Order Status',
        messages: [
          { type: 'user' as const, text: "I need a status update on PO number 78432. It was supposed to ship last Friday.", delay: 300 },
          { type: 'genie' as const, text: "Let me pull that up. PO-78432 — I see it's for 2,000 units of part number HX-750. It shipped yesterday via freight and is currently in transit.", delay: 1200 },
          { type: 'user' as const, text: "Finally. Do you have a tracking number?", delay: 2500 },
          { type: 'genie' as const, text: "Yes — the PRO number is FG-284719. Estimated delivery is Thursday. I can send the full tracking details to your email. Would you like that?", delay: 3400 },
          { type: 'user' as const, text: "Yes, send it to purchasing@acmecorp.com", delay: 4600 },
          { type: 'genie' as const, text: "Done — tracking details sent to purchasing@acmecorp.com. You'll also receive automatic updates when it arrives at the local terminal and when delivery is scheduled. Anything else on this order?", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Order located by PO number', delay: 6500 },
          { id: '2', text: 'Ship date and carrier confirmed', delay: 6700 },
          { id: '3', text: 'Tracking number provided', delay: 6900 },
          { id: '4', text: 'Tracking details emailed', delay: 7100 },
          { id: '5', text: 'Auto-updates enabled', delay: 7300 },
        ],
      },
      {
        id: 'technical_issue',
        title: 'Technical Support',
        messages: [
          { type: 'user' as const, text: "We're getting inconsistent torque readings from the HX-750 units we just installed. Readings are fluctuating between 45 and 60 Nm.", delay: 300 },
          { type: 'genie' as const, text: "That fluctuation range is outside normal spec. Let me walk through a few checks. First — are you using the recommended mounting torque of 25 Nm for the housing bolts? Under-torquing can cause vibration that affects readings.", delay: 1200 },
          { type: 'user' as const, text: "Let me check... actually I think we torqued to 20 Nm based on an older spec sheet", delay: 2500 },
          { type: 'genie' as const, text: "That's likely your issue — the HX-750 spec was updated to 25 Nm in revision 3. Re-torquing to 25 Nm should resolve the fluctuation. I can send you the current spec sheet. Would you also like me to create a ticket so our engineering team follows up?", delay: 3400 },
          { type: 'user' as const, text: "Yes to both. We have 50 units installed so we'll need to re-torque them all.", delay: 4600 },
          { type: 'genie' as const, text: "Spec sheet sent. I've also created a technical support ticket — TSR-8847 — noting 50 units affected. Our field applications engineer will contact you within 24 hours to assist with the re-torque procedure if needed.", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Technical issue triaged', delay: 6500 },
          { id: '2', text: 'Root cause identified (spec revision)', delay: 6700 },
          { id: '3', text: 'Updated documentation sent', delay: 6900 },
          { id: '4', text: 'Support ticket created (TSR-8847)', delay: 7100 },
          { id: '5', text: 'Engineering follow-up scheduled', delay: 7300 },
        ],
      },
      {
        id: 'rfq_capture',
        title: 'Quote Request',
        messages: [
          { type: 'user' as const, text: "We need a quote for a custom bracket. Aluminum 6061-T6, about 500 units, with a pretty tight tolerance on the mounting holes.", delay: 300 },
          { type: 'genie' as const, text: "I can get that started. For an accurate quote I'll need a few details. Do you have a drawing or CAD file you can share, and what tolerance are we talking for the mounting holes?", delay: 1200 },
          { type: 'user' as const, text: "Plus or minus .002 on the holes. I can email the STEP file.", delay: 2500 },
          { type: 'genie' as const, text: "+/- .002 is within our standard CNC capability. What's your target delivery timeline, and is this a one-time run or recurring?", delay: 3400 },
          { type: 'user' as const, text: "Recurring — we'd need the first batch in 6 weeks, then 500 per month. Send the file to quotes@? And I'm Brian Park at Nexus Dynamics.", delay: 4600 },
          { type: 'genie' as const, text: "Perfect, Brian. Send the STEP file to quotes@ourcompany.com and reference RFQ-9912. Our quoting team will have pricing back to you within 48 hours covering the initial run and monthly recurring. Thanks for reaching out, Nexus Dynamics!", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Material and spec captured', delay: 6500 },
          { id: '2', text: 'Tolerance verified against capability', delay: 6700 },
          { id: '3', text: 'Volume and cadence documented', delay: 6900 },
          { id: '4', text: 'RFQ number assigned (RFQ-9912)', delay: 7100 },
          { id: '5', text: 'Qualified B2B lead created', delay: 7300 },
        ],
      },
      {
        id: 'quality_issue',
        title: 'Quality Report',
        messages: [
          { type: 'user' as const, text: "We received lot 2024-0456 and found 12 out of 500 units with surface finish defects — scratches on the anodized coating.", delay: 300 },
          { type: 'genie' as const, text: "I'm sorry to hear about the finish defects. I'm creating a quality report right now. Can you describe the scratch pattern — are they consistent across all 12 units, or random?", delay: 1200 },
          { type: 'user' as const, text: "They look consistent, like a handling issue. All scratches are on the same face.", delay: 2500 },
          { type: 'genie' as const, text: "That helps — consistent pattern on the same face suggests a process issue rather than material defect. I've logged this as QR-2024-0892. Do you need replacement units, or can you work with the unaffected 488?", delay: 3400 },
          { type: 'user' as const, text: "We need all 500. Can you expedite 12 replacements?", delay: 4600 },
          { type: 'genie' as const, text: "I've flagged 12 replacement units as expedited on QR-2024-0892. Our quality team will review and approve the replacement within 24 hours, and we'll ship them priority. You should have them within 3–4 business days.", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Defect details documented', delay: 6500 },
          { id: '2', text: 'Root cause pattern identified', delay: 6700 },
          { id: '3', text: 'Quality report created (QR-2024-0892)', delay: 6900 },
          { id: '4', text: 'Replacement units expedited', delay: 7100 },
          { id: '5', text: 'Customer kept on production schedule', delay: 7300 },
        ],
      },
      {
        id: 'after_hours_shipping',
        title: 'After Hours',
        messages: [
          { type: 'user' as const, text: "It's 10 PM here but I'm calling from our plant in Germany. We're running short on part GX-200 and our production line stops tomorrow if we don't get a shipment.", delay: 300 },
          { type: 'genie' as const, text: "I understand the urgency — a production line shutdown is critical. Let me check inventory. I show 850 units of GX-200 available in our Ohio warehouse. How many do you need?", delay: 1200 },
          { type: 'user' as const, text: "200 units minimum. Can you get them to Frankfurt?", delay: 2500 },
          { type: 'genie' as const, text: "200 units is available. For international expedited shipping to Frankfurt, I'll flag this for our logistics team first thing US morning — that's about 5 hours from now. They can arrange air freight for estimated 2-day delivery.", delay: 3400 },
          { type: 'user' as const, text: "That's tight but could work. This is Hans Weber, account number GER-4412.", delay: 4600 },
          { type: 'genie' as const, text: "Noted, Hans. I've created an urgent order request for 200x GX-200 to Frankfurt, account GER-4412, flagged for air freight. Our logistics team will contact you by 8 AM CET with shipping confirmation and tracking.", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'International customer served after hours', delay: 6500 },
          { id: '2', text: 'Inventory availability confirmed', delay: 6700 },
          { id: '3', text: 'Urgent order flagged for logistics', delay: 6900 },
          { id: '4', text: 'Account verified (GER-4412)', delay: 7100 },
          { id: '5', text: 'Production line shutdown averted', delay: 7300 },
        ],
      },
    ],
    useCasesHeader: {
      title: "Stop Losing Orders to Silence",
      subtitle: "Your customers run second and third shifts. When they call at 11 PM for an order update or at 5 AM with a quality issue, your Genie is there.",
    },
    useCaseCards: [
      {
        id: 'order-management',
        title: 'Order Status & Tracking',
        description: "PO status, ship dates, tracking numbers, delivery ETAs — your Genie pulls it from your ERP and delivers it instantly, day or night.",
        bullets: [
          'Real-time order status from SAP, NetSuite, or Epicor',
          'Tracking numbers and delivery estimates',
          'Automated shipment notifications',
          'Reduce order status calls by 50%',
        ],
        image: '/solutions-pages/manufacturing/usecase-order-tracking.webp',
      },
      {
        id: 'technical-triage',
        title: 'Technical Support Triage',
        description: "Product specs, installation guidance, troubleshooting — your Genie handles front line technical calls so your engineers focus on complex issues.",
        bullets: [
          'Spec sheet and documentation delivery',
          'Guided troubleshooting procedures',
          'Warranty verification and claims',
          'Escalation to engineers with full context',
        ],
        image: '/solutions-pages/manufacturing/usecase-tech-support.webp',
      },
      {
        id: 'rfq-intake',
        title: 'RFQ & Quote Capture',
        description: "Capture detailed quote requests after hours with material specs, tolerances, volumes, and timelines — ready for your quoting team in the morning.",
        bullets: [
          'Material and tolerance capture',
          'Volume and cadence documentation',
          'Drawing/file submission instructions',
          '35% faster quote turnaround',
        ],
        image: '/solutions-pages/manufacturing/usecase-rfq-capture.webp',
      },
      {
        id: 'quality-reporting',
        title: 'Quality Issue Management',
        description: "Customers report defects, your Genie documents everything — lot numbers, defect descriptions, quantities — and kicks off the resolution process.",
        bullets: [
          'Structured defect intake',
          'Lot and batch tracking',
          'Replacement orders initiated',
          'Quality feedback loop improved',
        ],
        image: '/solutions-pages/manufacturing/usecase-quality-reporting.webp',
      },
    ],
    finalCTA: {
      title: "Your Factory Floor Never Sleeps",
      subtitle: "Your customers run around the clock, across time zones. Your Genie makes sure every order inquiry, technical question, and quality issue gets handled — instantly.",
      pillars: [
        {
          title: 'Always\nTracking',
          gradient: 'from-purple-400 to-blue-400',
          description: 'Order status, shipping updates, and delivery confirmations — pulled live from your ERP.',
        },
        {
          title: 'Always\nSolving',
          gradient: 'from-blue-400 to-cyan-400',
          description: 'Technical issues triaged, specs delivered, and engineers notified — before you open.',
        },
        {
          title: 'Always\nCapturing',
          gradient: 'from-cyan-400 to-teal-400',
          description: 'RFQs, quality reports, and new business — captured from every time zone.',
        },
      ],
      backgroundImage: '/solutions-pages/manufacturing/cta-background.webp',
    },
  }
};
